import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { userApi } from '../services/cloudApi';
import {
  getLockActive,
  setLockActive,
  clearLockActive,
  clearAllLockActive,
} from '../utils/lockActiveStore';

const LockContext = createContext(null);

const AUTH_ROUTES = ['/login', '/register', '/forgot-password', '/reset-password'];

export const LockProvider = ({ children }) => {
  const { user, authToken, isAuthenticated } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  // isLocked mirrors device-local lockActive — NEVER derived from hasPin alone
  const [isLocked, setIsLocked] = useState(false);
  const [hasPin, setHasPin] = useState(false);
  const [isCheckingPin, setIsCheckingPin] = useState(true);
  const [previousRoute, setPreviousRoute] = useState(null);
  const [lockHydrated, setLockHydrated] = useState(false);

  const userId = user?.id;

  // Hydrate lockActive from device storage when user is known
  useEffect(() => {
    if (!isAuthenticated || !userId) {
      setIsLocked(false);
      setLockHydrated(true);
      setIsCheckingPin(false);
      return;
    }
    const active = getLockActive(userId);
    setIsLocked(active);
    setLockHydrated(true);
  }, [isAuthenticated, userId]);

  const checkPinStatus = useCallback(async () => {
    if (!isAuthenticated || !authToken || !userId) {
      setIsCheckingPin(false);
      return;
    }

    try {
      const result = await userApi.getPin({ token: authToken });
      if (result?.ok) {
        setHasPin(result.hasPin || false);
      }
    } catch (error) {
      console.error('Check PIN status error:', error);
      setHasPin(false);
    } finally {
      setIsCheckingPin(false);
    }
  }, [isAuthenticated, authToken, userId]);

  useEffect(() => {
    checkPinStatus();
  }, [checkPinStatus]);

  // Manual lock only — persists lockActive for this device
  const lock = useCallback(() => {
    if (!userId) return;
    setPreviousRoute(location.pathname !== '/lock' ? location.pathname : previousRoute);
    setLockActive(userId, true);
    setIsLocked(true);
    navigate('/lock', { replace: true });
  }, [userId, location.pathname, navigate, previousRoute]);

  const unlock = useCallback(() => {
    if (userId) {
      clearLockActive(userId);
    }
    setIsLocked(false);
    if (location.pathname === '/lock') {
      if (previousRoute && previousRoute !== '/lock') {
        navigate(previousRoute, { replace: true });
      } else {
        navigate('/notes', { replace: true });
      }
      setPreviousRoute(null);
    }
  }, [userId, location.pathname, navigate, previousRoute]);

  const verifyPin = useCallback(
    async (pin) => {
      if (!authToken) {
        return { ok: false, error: 'NOT_AUTHENTICATED' };
      }

      try {
        const result = await userApi.verifyPin({ token: authToken, pin });
        if (result?.ok && result.verified) {
          unlock();
          return { ok: true, verified: true };
        }
        if (result?.error === 'PIN_VERIFY_LOCKED' || result?.payload?.error === 'PIN_VERIFY_LOCKED') {
          return {
            ok: false,
            error: 'PIN_VERIFY_LOCKED',
            retryAfterSeconds:
              result?.retryAfterSeconds || result?.payload?.retryAfterSeconds || 900,
            remainingAttempts: 0,
          };
        }
        return {
          ok: true,
          verified: false,
          remainingAttempts: result?.remainingAttempts,
        };
      } catch (error) {
        const code = error?.payload?.error || error?.code || error?.message;
        if (code === 'PIN_VERIFY_LOCKED') {
          return {
            ok: false,
            error: 'PIN_VERIFY_LOCKED',
            retryAfterSeconds: error?.payload?.retryAfterSeconds || 900,
            remainingAttempts: 0,
          };
        }
        console.error('Verify PIN error:', error);
        return { ok: false, error: code || 'VERIFY_PIN_ERROR' };
      }
    },
    [authToken, unlock]
  );

  /**
   * @param {string} pin
   * @param {{ currentPin?: string }} [options] — currentPin required by API when changing
   */
  const setPin = useCallback(
    async (pin, options = {}) => {
      if (!authToken) {
        return { ok: false, error: 'NOT_AUTHENTICATED' };
      }

      try {
        const result = await userApi.setPin({
          token: authToken,
          pin,
          currentPin: options.currentPin,
        });
        if (result?.ok) {
          setHasPin(true);
          return { ok: true };
        }
        return { ok: false, error: result?.error || 'SET_PIN_ERROR' };
      } catch (error) {
        const code = error?.payload?.error || error?.message || 'SET_PIN_ERROR';
        console.error('Set PIN error:', error);
        return { ok: false, error: code };
      }
    },
    [authToken]
  );

  const removePin = useCallback(
    async (currentPin) => {
      if (!authToken) {
        return { ok: false, error: 'NOT_AUTHENTICATED' };
      }
      try {
        const result = await userApi.removePin({ token: authToken, currentPin });
        if (result?.ok) {
          setHasPin(false);
          if (userId) clearLockActive(userId);
          setIsLocked(false);
          return { ok: true };
        }
        return { ok: false, error: result?.error || 'REMOVE_PIN_ERROR' };
      } catch (error) {
        const code = error?.payload?.error || error?.message || 'REMOVE_PIN_ERROR';
        console.error('Remove PIN error:', error);
        return { ok: false, error: code };
      }
    },
    [authToken, userId]
  );

  // Route guard: only when manually locked (persisted lockActive)
  useEffect(() => {
    if (!lockHydrated || !isLocked) return;
    const isAuthRoute = AUTH_ROUTES.some((r) => location.pathname.startsWith(r));
    if (location.pathname !== '/lock' && !isAuthRoute) {
      navigate('/lock', { replace: true });
    }
  }, [isLocked, lockHydrated, location.pathname, navigate]);

  // After hydrate: if locked and not on lock page, force lock route
  useEffect(() => {
    if (!lockHydrated || !isAuthenticated || !userId) return;
    if (isLocked && location.pathname !== '/lock') {
      const isAuthRoute = AUTH_ROUTES.some((r) => location.pathname.startsWith(r));
      if (!isAuthRoute) {
        navigate('/lock', { replace: true });
      }
    }
  }, [lockHydrated, isAuthenticated, userId, isLocked, location.pathname, navigate]);

  // Clear device lock on logout (PIN stays on account)
  useEffect(() => {
    const onLogout = () => {
      clearAllLockActive();
      setIsLocked(false);
      setHasPin(false);
      setPreviousRoute(null);
    };
    window.addEventListener('notesplus-user-logged-out', onLogout);
    return () => window.removeEventListener('notesplus-user-logged-out', onLogout);
  }, []);

  const value = {
    isLocked,
    hasPin,
    isCheckingPin,
    lockHydrated,
    lock,
    unlock,
    verifyPin,
    setPin,
    removePin,
    checkPinStatus,
  };

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.lockContext = { lock, isLocked, unlock };
      return () => {
        delete window.lockContext;
      };
    }
    return undefined;
  }, [lock, isLocked, unlock]);

  return <LockContext.Provider value={value}>{children}</LockContext.Provider>;
};

export const useLock = () => {
  const context = useContext(LockContext);
  if (!context) {
    throw new Error('useLock must be used within LockProvider');
  }
  return context;
};
